'use client';

import {
  Box,
  Button,
  Dialog,
  Divider,
  IconButton,
  SystemIcon,
  Typography,
} from '@wishket/design-system';
import { Fragment, ReactNode, useRef, useState } from 'react';
import { useClickOutside } from '@wishket/yogokit';
import { twJoin } from 'tailwind-merge';

import { useAskAIQuestionListQuery } from '@/entities/askAi/models';
import { useDeviceCheck } from '@/shared/hooks';
import { ArticleItemProps } from '@/shared/types';
import { Icon, ShareBox } from '@/shared/Components';
import {
  BottomSheetRemovableList,
  RemovableList,
} from '@/features/RemovableList';
import { ArticleItem } from '@/features/Article';
import { openNavigatorShare } from '@/shared/utils';

interface QuestionHistoryListProps {
  onHistoryClick: (hashKey: string) => void;
  onHistoryDelete: (hashKey: string) => void;
  onEmptyItemClick: () => void;
}

export const QuestionHistoryList = ({
  onHistoryClick,
  onHistoryDelete,
  onEmptyItemClick,
}: QuestionHistoryListProps) => {
  const [selectedHashKey, setSelectedHashKey] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const { data: questions } = useAskAIQuestionListQuery();

  const openBottomSheet = () => {
    setIsBottomSheetOpen(true);
  };

  const closeBottomSheet = () => {
    setIsBottomSheetOpen(false);
  };

  const list = questions?.map(({ hashKey, questionText }) => ({
    text: questionText,
    onClick: () => {
      onHistoryClick(hashKey);
      closeBottomSheet();
    },
    onRemove: () => {
      setSelectedHashKey(hashKey);
      setIsDeleteDialogOpen(true);
    },
  }));

  const handleHistoryDelete = () => {
    selectedHashKey && onHistoryDelete(selectedHashKey);
    setSelectedHashKey(null);
    setIsDeleteDialogOpen(false);

    const isLastItem = list && list.length === 1;
    isLastItem && closeBottomSheet();
  };

  const handleBottomSheetEmptyItemClick = () => {
    closeBottomSheet();
    onEmptyItemClick();
  };

  return (
    <>
      <Box className="hidden desktop:block">
        <RemovableList list={list} onEmptyItemClick={onEmptyItemClick} />
      </Box>
      <Box className="shrink-0 desktop:hidden">
        <Button onClick={openBottomSheet} className="rounded-full" size="sm">
          <SystemIcon name="small_list" className="text-w-gray-600" />
          <Typography variant="body2" className="text-w-gray-600">
            목록
          </Typography>
        </Button>
        <BottomSheetRemovableList
          list={list}
          isOpen={isBottomSheetOpen}
          onClose={closeBottomSheet}
          onEmptyItemClick={handleBottomSheetEmptyItemClick}
        />
      </Box>
      <Dialog
        close="취소"
        title="질문 삭제"
        confirm="삭제하기"
        variant="caution"
        description={`질문을 삭제할까요?\n삭제된 질문은 복구할 수 없습니다.`}
        isOpen={isDeleteDialogOpen}
        onConfirm={handleHistoryDelete}
        onClose={() => setIsDeleteDialogOpen(false)}
      />
    </>
  );
};

const AskAIArticleItemWidget = ({
  id,
  href,
  title,
  category,
  categoryHref,
  description,
  thumbnail,
  articleInfo,
  author,
  isVisibleCategory,
  isVisibleUtil,
  openInNewTab,
  onClickDelete,
}: ArticleItemProps) => {
  return (
    <Box
      className="relative"
      id={`askAI-article-item--${id}`}
      data-gtm-id="askAI-article-item"
    >
      <ArticleItem
        id={id}
        href={href}
        title={title}
        category={category}
        categoryHref={categoryHref}
        description={description}
        thumbnail={thumbnail}
        articleInfo={articleInfo}
        author={author}
        isVisibleCategory={isVisibleCategory}
        isVisibleUtil={isVisibleUtil}
        onClickDelete={onClickDelete}
        openInNewTab={openInNewTab}
      />
    </Box>
  );
};

interface AskAIResponseCardProps {
  text: string;
  contentsItems: ArticleItemProps[];
  shareUrl?: string;
  shareTitle?: string;
}

export const AskAIResponseCard = ({
  text,
  contentsItems,
  shareUrl,
  shareTitle,
}: AskAIResponseCardProps) => {
  const { isDesktop } = useDeviceCheck();
  const iconShareBoxRef = useRef<HTMLDivElement>(null);
  const buttonShareBoxRef = useRef<HTMLDivElement>(null);
  const [isIconShareBoxVisible, setIsIconShareBoxVisible] = useState(false);
  const [isButtonShareBoxVisible, setIsButtonShareBoxVisible] = useState(false);
  const isShareDataExist = shareUrl && shareTitle;

  const toggleIconShareBox = () => {
    setIsIconShareBoxVisible((prev) => !prev);
  };

  const toggleButtonShareBox = () => {
    if (isDesktop) {
      setIsButtonShareBoxVisible((prev) => !prev);
    }
    if (!isDesktop && isShareDataExist) {
      openNavigatorShare({ url: shareUrl, title: shareTitle });
    }
  };

  useClickOutside(iconShareBoxRef, toggleIconShareBox);
  useClickOutside(buttonShareBoxRef, toggleButtonShareBox);

  return (
    <>
      <Box
        className={twJoin(
          'flex flex-col items-center gap-12 pb-12',
          'desktop:rounded-3xl desktop:bg-w-white desktop:px-6 desktop:pt-6'
        )}
      >
        <Box className="flex w-full items-start justify-between desktop:p-0">
          <Box className="flex gap-5 px-5 desktop:px-0">
            <Box className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-primary-0 desktop:size-10">
              <SystemIcon
                name="medium_answer"
                className="text-w-white desktop:hidden"
              />
              <SystemIcon
                name="large_answer"
                className="hidden text-w-white desktop:block"
              />
            </Box>
            <Typography
              variant="contents2"
              className="whitespace-pre-line pt-0.5 text-justify text-w-gray-800 desktop:pt-[5px]"
            >
              {text}
            </Typography>
          </Box>
          {isShareDataExist && (
            <Box className="relative hidden shrink-0 desktop:block">
              <IconButton size="lg" onClick={toggleIconShareBox}>
                <SystemIcon name="large_share" />
              </IconButton>
              {isIconShareBoxVisible && (
                <ShareBox
                  ref={iconShareBoxRef}
                  url={shareUrl}
                  title={shareTitle}
                  className="absolute left-full mt-2 -translate-x-full transform"
                />
              )}
            </Box>
          )}
        </Box>
        <Box className="flex w-full flex-col gap-6 rounded-3xl bg-w-gray-10 p-6 desktop:p-8">
          <Box className="flex items-center gap-2">
            <Typography
              variant="subTitle2"
              className="font-medium text-w-gray-900 desktop:typo-subTitle1"
            >
              열심히 읽고 답변했어요!
            </Typography>
            <Icon name="heart_face" />
          </Box>
          <Box className="flex flex-col gap-6">
            {contentsItems.map((contentsItem, index) => {
              const contentsItemPropsExceptDate = {
                ...contentsItem,
                articleInfo: {
                  ...contentsItem.articleInfo,
                  date: undefined,
                },
              };

              return (
                <Fragment key={contentsItem.id}>
                  {index > 0 && <Divider />}
                  <AskAIArticleItemWidget
                    isVisibleCategory
                    openInNewTab
                    {...contentsItemPropsExceptDate}
                  />
                </Fragment>
              );
            })}
          </Box>
        </Box>
        {isShareDataExist && (
          <Box className="relative">
            <Button
              variant="outlined"
              className="text-w-gray-600"
              onClick={toggleButtonShareBox}
            >
              <SystemIcon name="medium_share" className="text-w-gray-600" />
              <Typography variant="body1">답변 공유하기</Typography>
            </Button>
            {isButtonShareBoxVisible && (
              <ShareBox
                ref={buttonShareBoxRef}
                url={shareUrl}
                title={shareTitle}
                className="absolute left-1/2 mt-2 -translate-x-1/2 transform"
              />
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

interface LoadingCardContainerProps {
  children: ReactNode;
}

export const LoadingCardContainer = ({
  children,
}: LoadingCardContainerProps) => {
  return (
    <Box className="flex flex-col gap-6 rounded-3xl bg-w-white p-5 desktop:gap-12 desktop:p-6">
      {children}
    </Box>
  );
};
